/**
 * Accounts under Spend (ADR 0067): which account or provider each session ran on. Helpers for
 * the account picker, the by-account panels and the rename dialog.
 */
import type { AccountBilling, AccountBucket, AccountKind, AccountSource, CostsState, SpendAccount } from '@orgus/protocol';
import { ACCOUNT_BILLING_LABEL, ACCOUNT_KIND_LABEL, UNKNOWN_ACCOUNT, UNKNOWN_ACCOUNT_LABEL, showApiKeyHint } from '@orgus/protocol';
import { Building2, CircleDashed, Cloud, Info, KeyRound } from '@orgus/ui/icons';
import type { LucideIcon } from '@orgus/ui/icons';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Button, Dialog, ErrorState, Input, cn, formatMoney, toUiError } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { renameAccount } from './useCosts.ts';

const ICON: Record<AccountKind, LucideIcon> = { anthropic: Building2, foundry: Cloud, bedrock: Cloud, vertex: Cloud, 'api-key': KeyRound, chatgpt: Building2, openai: KeyRound, google: Building2, gemini: KeyRound };

/** A small tile with the account kind's icon, the lead of an account row. */
export function AccountGlyph({ kind, className }: { kind: AccountKind | null; className?: string }) {
  const Icon = kind ? ICON[kind] : CircleDashed;
  return (
    <span aria-hidden className={cn('flex size-5 shrink-0 items-center justify-center rounded-control bg-active text-fg-muted', className)}>
      <Icon className="size-3" />
    </span>
  );
}

/** What a figure of this billing means, in one or two words. */
export const BILLING_SHORT: Record<AccountBilling, string> = { subscription: 'Notional', usage: 'List price', unknown: 'Org setting' };

export const SOURCE_LABEL: Record<AccountSource, string> = {
  'credential-org': 'Transcript',
  'launch-env': 'Launch settings',
  config: 'Sign-in',
  history: 'Sign-in history',
  'message-id': 'Message ids',
  'provider-report': 'Provider report',
};

/**
 * The dim fact after an account's name in a by-account list: what its figure means, "Sign-in
 * history" when that weak source filed most of it, or an API key's usage the provider report
 * has but no session holds (ADR 0067, amended 19 Oct 2026). `short` is the form for a narrow
 * row (the name wins the width), `hint` the whole story on hover.
 */
export function accountMeta(b: Pick<AccountBucket, 'key' | 'billing' | 'source' | 'movedOut' | 'unmatched'>): { text: string; short: string; hint: string | null } {
  if (!b.key) return { text: 'No signal yet', short: 'No signal', hint: null };
  const moved = b.movedOut ? ` ${formatMoney(b.movedOut)} moved to API keys.` : '';
  if (b.unmatched) {
    const money = formatMoney(b.unmatched);
    return { text: `${money} not matched`, short: `+${money}`, hint: `${money} of its reported usage is not matched to a session.` };
  }
  if (b.source === 'history') return { text: SOURCE_LABEL.history, short: 'History', hint: `${SOURCE_LABEL.history}, ${BILLING_SHORT[b.billing].toLowerCase()}.${moved}` };
  if (b.source === 'provider-report') return { text: BILLING_SHORT[b.billing], short: BILLING_SHORT[b.billing], hint: `${BILLING_SHORT[b.billing]}, from the provider report.` };
  return { text: BILLING_SHORT[b.billing], short: BILLING_SHORT[b.billing], hint: moved ? `${BILLING_SHORT[b.billing]}.${moved}` : null };
}

/**
 * The quiet line under By account and Settings, Spend, Accounts (showApiKeyHint): without the
 * provider report, API-key spend sits under the sign-in.
 */
export function ApiKeyHint({ costs, className }: { costs: CostsState | undefined; className?: string }) {
  if (!showApiKeyHint(costs)) return null;
  return (
    <p className={cn('flex min-w-0 items-start gap-2 text-caption text-fg-subtle', className)}>
      <span aria-hidden className="flex h-4 shrink-0 items-center">
        <Info className="size-3" />
      </span>
      <span className="min-w-0">
        Claude Code does not record API-key use.{' '}
        <Link to="/spend/reconcile" className="text-fg-muted underline underline-offset-2 hover:text-fg">
          Connect the usage report
        </Link>{' '}
        to split it out.
      </span>
    </p>
  );
}

export function kindLabel(kind: AccountKind | null): string {
  return kind ? ACCOUNT_KIND_LABEL[kind] : UNKNOWN_ACCOUNT_LABEL;
}

export function billingLabel(b: AccountBilling): string {
  return ACCOUNT_BILLING_LABEL[b];
}

export function accountName(a: Pick<SpendAccount, 'name' | 'label'>): string {
  return a.name ?? a.label;
}

/** The account picker is worth showing once there are two accounts to tell apart. */
export function hasAccounts(costs: CostsState | undefined): boolean {
  return (costs?.accounts?.length ?? 0) >= 2;
}

/** Options for the account picker: every account, then the unknown one when it has spend. */
export function accountOptions(costs: CostsState | undefined): { value: string; label: string }[] {
  const list = costs?.accounts ?? [];
  const unknown = costs?.byAccount?.some((b) => b.key === '') ?? false;
  return [
    { value: '', label: 'All accounts' },
    ...[...list].sort((a, b) => accountName(a).localeCompare(accountName(b))).map((a) => ({ value: a.key, label: accountName(a) })),
    ...(unknown ? [{ value: UNKNOWN_ACCOUNT, label: UNKNOWN_ACCOUNT_LABEL }] : []),
  ];
}

/** Do the accounts mix seat subscriptions with usage billing (so figures mix notional and list price)? */
export function mixedBilling(costs: CostsState): boolean {
  const kinds = new Set((costs.accounts ?? []).map((a) => a.billing));
  return kinds.has('subscription') && kinds.has('usage');
}

/** Rename an account, or go back to its automatic name with an empty one. */
export function RenameAccountDialog({ account, onOpenChange }: { account: SpendAccount | null; onOpenChange(open: boolean): void }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  useEffect(() => {
    if (!account) return;
    setName(account.name ?? '');
    setError(null);
  }, [account]);
  const submit = async () => {
    if (!account) return;
    setBusy(true);
    setError(null);
    try {
      await renameAccount(account.key, name.trim());
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e, { what: 'The account was not renamed.' }));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={!!account}
      onOpenChange={onOpenChange}
      dismissible={!busy}
      title="Rename account"
      description={account ? `Shown instead of ${account.label} everywhere in Spend. Leave it empty to use that name again.` : undefined}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()}>
            Save name
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Input label="Name" placeholder={account?.label} value={name} onChange={(e) => setName(e.target.value)} maxLength={60} autoFocus />
      </form>
      {error && <ErrorState error={error} variant="inline" className="mt-3" />}
    </Dialog>
  );
}
